import { Float32BufferAttribute, Vector3 } from 'three';
import type { Vertex } from '@thirdPartyLibraries/three-mesh-halfedge/src';
import type HalfEdgeMesh from '@three/common/halfEdgeMesh/HalfEdgeMesh';

/**
 * Closes a border "bay" by filling it with a ladder patch between its two
 * shores.
 *
 * `arc` is the ordered border path along the bay coastline, mouth-to-mouth
 * (first and last vertex are the bay opening). Walking both shores from the
 * mouth inward pairs opposite coastline vertices into "rungs"; each rung gets
 * interior vertices at the coastline's mean edge length, and consecutive
 * rungs are stitched with a greedy triangulation. The patch interior is then
 * Laplacian-relaxed, so the fill comes out as a regular, smooth grid instead
 * of the long skinny fan triangles a vertex-less zipper produces.
 *
 * The patch is appended at the GEOMETRY level (position/color/index buffers +
 * setNewGeometry rebuild), mirroring sealOuterBorderLoop's cap: the half-edge
 * DS's addFace splicing silently no-ops or builds parallel edges on boundary
 * corners, so in-place face insertion cannot be trusted here. Rebuilding
 * pairs the new triangles' edges as twins purely from the index buffer.
 *
 * NOTE: the rebuild replaces every Vertex object of the mesh — callers must
 * drop any Vertex references they hold, or pass their vertex-keyed sets via
 * `preserveVertexSets` to have them remapped in place onto the new vertices.
 *
 * Returns the number of triangles appended.
 */
export function zipBorderArc(
  mesh: HalfEdgeMesh,
  arc: Vertex[],
  smoothIterations = 6,
  preserveVertexSets: Array<Set<Vertex>> = [],
): number {
  if (arc.length < 4) return 0;

  // Orient the arc along the hole's free-boundary circulation: appended
  // triangles then traverse each shore edge OPPOSITE to its existing face,
  // which is what the rebuilt DS needs to pair them as twins.
  const oriented = arc[0].getHalfedgeToVertex(arc[1])?.face === null ? arc : [...arc].reverse();

  // Buffer indices in toBufferGeometry's vertex order (same trick as the seal cap).
  const vertexToIndex = new Map<Vertex, number>();
  let bufferIndex = 0;
  for (const v of mesh.halfEdges.vertices) vertexToIndex.set(v, bufferIndex++);

  const geometry = mesh.toBufferGeometry();
  const indexAttr = geometry.getIndex();
  if (!indexAttr) return 0;
  const positions = Array.from(geometry.getAttribute('position').array as ArrayLike<number>);
  const colors = Array.from(geometry.getAttribute('color').array as ArrayLike<number>);
  const indices = Array.from(indexAttr.array as ArrayLike<number>);

  // Interior spacing target: the coastline's mean edge length.
  let arcLen = 0;
  for (let i = 0; i < oriented.length - 1; i++) {
    arcLen += oriented[i].position.distanceTo(oriented[i + 1].position);
  }
  const spacing = Math.max(0.15, arcLen / (oriented.length - 1));

  // setFromGeometry welds coincident positions (tolerance 1e-10), so an
  // interior point landing exactly on an existing vertex would collapse into
  // it and leave a degenerate triangle — skip such points instead.
  const occupied = new Set<string>();
  const positionKey = (x: number, y: number, z: number) =>
    `${Math.round(x * 1e4)},${Math.round(y * 1e4)},${Math.round(z * 1e4)}`;
  for (let i = 0; i < positions.length; i += 3) {
    occupied.add(positionKey(positions[i], positions[i + 1], positions[i + 2]));
  }

  // Build the rungs: existing shore vertices at the ends, new interior
  // vertices (appended to the buffers) in between.
  const newVertexIds: number[] = [];
  const appendVertex = (p: Vector3, ca: number, cb: number, t: number): number => {
    const id = positions.length / 3;
    positions.push(p.x, p.y, p.z);
    colors.push(
      colors[ca * 3] + (colors[cb * 3] - colors[ca * 3]) * t,
      colors[ca * 3 + 1] + (colors[cb * 3 + 1] - colors[ca * 3 + 1]) * t,
      colors[ca * 3 + 2] + (colors[cb * 3 + 2] - colors[ca * 3 + 2]) * t,
    );
    newVertexIds.push(id);
    return id;
  };

  const rungs: number[][] = [];
  const lerped = new Vector3();
  // When a rung's interior would land on existing vertices (a chord running
  // along the coastline, e.g. a flat bay bottom), the ladder stops there and
  // the remaining sub-arc is closed with the plain vertex-less fan zipper.
  let fanFrom: [number, number] | null = null;
  for (let i = 0, j = oriented.length - 1; j - i >= 0; i++, j--) {
    const left = oriented[i];
    const right = oriented[j];
    const leftId = vertexToIndex.get(left);
    const rightId = vertexToIndex.get(right);
    if (leftId === undefined || rightId === undefined) return 0;
    if (j - i === 0) {
      rungs.push([leftId]);
      break;
    }
    if (j - i === 1) {
      // Adjacent coastline vertices — their existing border edge closes the patch.
      rungs.push([leftId, rightId]);
      break;
    }
    const width = left.position.distanceTo(right.position);
    const interior = Math.min(64, Math.max(0, Math.round(width / spacing) - 1));
    const candidates: Vector3[] = [];
    let collides = false;
    for (let k = 1; k <= interior; k++) {
      lerped.copy(left.position).lerp(right.position, k / (interior + 1));
      if (occupied.has(positionKey(lerped.x, lerped.y, lerped.z))) {
        collides = true;
        break;
      }
      candidates.push(lerped.clone());
    }
    if (collides) {
      rungs.push([leftId, rightId]);
      fanFrom = [i, j];
      break;
    }
    const rung = [leftId];
    for (let k = 0; k < candidates.length; k++) {
      const p = candidates[k];
      occupied.add(positionKey(p.x, p.y, p.z));
      rung.push(appendVertex(p, leftId, rightId, (k + 1) / (candidates.length + 1)));
    }
    rung.push(rightId);
    rungs.push(rung);
  }
  if (rungs.length < 2 && !fanFrom) return 0;

  // Greedy stitch of consecutive rungs, picking the shorter diagonal. The
  // winding makes every bay boundary edge appear along the arc's circulation
  // (left shore P0→Q0, right shore Qlast→Plast), so the rebuild pairs each
  // shore edge with its existing face as twins.
  const pa = new Vector3();
  const pb = new Vector3();
  const point = (id: number, out: Vector3) =>
    out.set(positions[id * 3], positions[id * 3 + 1], positions[id * 3 + 2]);
  let added = 0;
  const emit = (a: number, b: number, c: number) => {
    indices.push(a, b, c);
    added++;
  };
  for (let r = 0; r + 1 < rungs.length; r++) {
    const P = rungs[r];
    const Q = rungs[r + 1];
    let a = 0;
    let b = 0;
    while (a < P.length - 1 || b < Q.length - 1) {
      if (b === Q.length - 1) {
        emit(P[a], Q[b], P[a + 1]);
        a++;
      } else if (a === P.length - 1) {
        emit(P[a], Q[b], Q[b + 1]);
        b++;
      } else {
        const advanceP = point(P[a + 1], pa).distanceToSquared(point(Q[b], pb));
        const advanceQ = point(Q[b + 1], pa).distanceToSquared(point(P[a], pb));
        if (advanceP <= advanceQ) {
          emit(P[a], Q[b], P[a + 1]);
          a++;
        } else {
          emit(P[a], Q[b], Q[b + 1]);
          b++;
        }
      }
    }
  }

  // Close the ladder's leftover sub-arc with the alternating fan zipper (its
  // last plain rung is the fan's mouth, so the regions share the chord edge).
  if (fanFrom) {
    let [i, j] = fanFrom;
    while (j - i >= 2) {
      emit(vertexToIndex.get(oriented[i])!, vertexToIndex.get(oriented[i + 1])!, vertexToIndex.get(oriented[j])!);
      i++;
      if (j - i >= 2) {
        emit(vertexToIndex.get(oriented[j - 1])!, vertexToIndex.get(oriented[j])!, vertexToIndex.get(oriented[i])!);
        j--;
      }
    }
  }
  if (added === 0) return 0;

  geometry.setAttribute('position', new Float32BufferAttribute(positions, 3));
  geometry.setAttribute('color', new Float32BufferAttribute(colors, 3));
  geometry.setIndex(indices);

  // Patch interior to relax AFTER the rebuild — resolved via renderIds
  // (buffer index = renderId), since the rebuild replaces all Vertex objects.
  const patchRenderIds = [
    ...oriented
      .slice(1, -1)
      .map((v) => vertexToIndex.get(v))
      .filter((id): id is number => id !== undefined),
    ...newVertexIds,
  ];

  // Capture caller-owned vertex sets as buffer indices so they survive the rebuild.
  const preservedIndices = preserveVertexSets.map((set) => {
    const ids: number[] = [];
    for (const v of set) {
      const idx = vertexToIndex.get(v);
      if (idx !== undefined) ids.push(idx);
    }
    return ids;
  });

  mesh.setNewGeometry(geometry);

  // Remap the preserved sets onto the freshly created vertices.
  preserveVertexSets.forEach((set, i) => {
    set.clear();
    for (const idx of preservedIndices[i]) {
      const v = mesh.halfEdges.renderIdToVertex.get(idx);
      if (v) set.add(v);
    }
  });

  if (smoothIterations > 0) {
    const patch = patchRenderIds
      .map((id) => mesh.halfEdges.renderIdToVertex.get(id))
      .filter((v): v is Vertex => !!v);
    relaxVertices(patch, smoothIterations);
    mesh.updateGeometryFromHalfedges();
  }

  return added;
}

/** Damped Jacobi Laplacian relaxation with a bounded fan walk per vertex. */
function relaxVertices(vertices: Vertex[], iterations: number): void {
  const neighborsOf = (v: Vertex, cap = 64): Vertex[] => {
    const out: Vertex[] = [];
    const start = v.halfedge;
    if (!start || start.vertex !== v) return out;
    let curr = start;
    for (let i = 0; i < cap; i++) {
      out.push(curr.twin.vertex);
      curr = curr.twin.next;
      if (curr === start) break;
    }
    return out;
  };

  const targets = new Map<Vertex, Vector3>();
  for (let iter = 0; iter < iterations; iter++) {
    targets.clear();
    for (const v of vertices) {
      const ns = neighborsOf(v);
      if (ns.length < 2) continue;
      const avg = new Vector3();
      for (const n of ns) avg.add(n.position);
      avg.divideScalar(ns.length);
      targets.set(v, v.position.clone().lerp(avg, 0.5));
    }
    targets.forEach((pos, v) => v.position.copy(pos));
  }
}
